import { Injectable, Injector, ElementRef } from '@angular/core';
import { Overlay, OverlayRef, OverlayConfig, PositionStrategy, OverlayPositionBuilder } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';

import { DateTime } from 'luxon';

import { PopupMenuComponent } from '../popup-menu.component';
import { PopupMenuConfig } from './popup-menu.config';
import { PopupMenuRef } from './popup-menu.ref';
import { PopupMenuItem } from './popup-menu-item.model';
import { AppConstants } from 'src/app/_apps/app.constants';
import { ColoredPopupMenuComponent } from '../colored/colored-popup-menu.component';

@Injectable({
    providedIn: 'root'
})
export class PopupMenuService
{
    public overlayRef: OverlayRef = null;
    private elementRef: ElementRef;
    private config: PopupMenuConfig;
    private popupMenuItems: PopupMenuItem[];
    private popupMenuRef: PopupMenuRef;
    private injector: Injector;
    private itemHeight: number; // px unit

    constructor (
        private overlay: Overlay,
        private parentInjector: Injector,
        protected overlayPositionBuilder: OverlayPositionBuilder
    )
    {
        this.itemHeight = parseInt(getComputedStyle(document.documentElement)
            .getPropertyValue('--popup-item-height'), 10);
    }

    create (source: ElementRef, config: PopupMenuConfig, items: PopupMenuItem[]): PopupMenuRef
    {
        if (this.popupMenuRef && this.popupMenuRef.isVisible())
        {
            return;
        }

        this.elementRef = source;
        this.config = new PopupMenuConfig();
        Object.assign(this.config, config);
        this.selectMenuItems(items);

        this.overlayRef = this.overlay.create(this.getOverlayConfig());
        this.popupMenuRef = new PopupMenuRef(items, this.overlayRef, this.config);
        this.injector = this.createInjector(this.popupMenuRef, this.parentInjector);

        if (this.config.isColored)
        {
            this.overlayRef.attach(new ComponentPortal(ColoredPopupMenuComponent, null, this.injector));
        }
        else
        {
            this.overlayRef.attach(new ComponentPortal(PopupMenuComponent, null, this.injector));
        }

        return this.popupMenuRef;
    }

    private createInjector (modalRef: PopupMenuRef, injector: Injector)
    {
        // PortalInjector a ete supprime de @angular/cdk 22 : remplacement par Injector.create.
        return Injector.create({
            providers: [{ provide: PopupMenuRef, useValue: modalRef }],
            parent: injector
        });
    }

    private getOverlayConfig (): OverlayConfig
    {
        const width = parseInt(getComputedStyle(document.documentElement)
            .getPropertyValue('--user-menu-width'), 10);

        return new OverlayConfig({
            panelClass:`${this.config.class}`,
            height: `${this.config.height}px`,
            minHeight: `${this.config.height}px`,
            width: this.config.width ? `${this.config.width}vw` : `${width}vw`,
            minWidth: this.config.width ? `${this.config.width}vw` : `${width}vw`,
            positionStrategy: this.getPositionStrategy()
        });
    }

    private getPositionStrategy (): PositionStrategy
    {
        return this.overlayPositionBuilder
            .flexibleConnectedTo(this.elementRef)
            .withPositions(this.config.positions)
            .withPush(false);
    }

    private selectMenuItems (items: PopupMenuItem[]): void
    {
        this.popupMenuItems = items
            .filter(pmi =>
            {
                if (AppConstants.IsProductionStage)
                {
                    return pmi.production ? pmi.production < DateTime.now() : true;
                }

                if (AppConstants.IsStagingStage)
                {
                    return pmi.staging ? pmi.staging < DateTime.now() : true;
                }

                return true;
            });
        this.popupMenuItems.sort((a: PopupMenuItem, b: PopupMenuItem) => a.order - b.order);
        const nbSeparators: number = this.popupMenuItems
            .filter((i) => i.separator)
            .length;
        this.config.height = (this.popupMenuItems.length + 1 + nbSeparators) * this.itemHeight;
    }
}
